import axios from 'axios';
import { useState, useEffect } from 'react';
import { EditorRiche } from '@/components/crm/EditorRiche';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import { usePermissions } from '@/lib/PermissionsContext';
import { GoogleMapsBtn } from '@/components/GoogleMapsBtn';
import { API_URL, calculerHeuresBudgetees, calculerCoutEquipe } from './utils';
import { Plus, ClipboardList, Home, TreePine, Trash2, Package, User, MapPin, ChevronDown, ChevronUp, Upload, Image as ImageIcon, CheckCircle, X, Calendar, ChevronLeft, ChevronRight, Clock, LogIn, LogOut as LogOutIcon, Edit, Edit3, Phone, Mail, Building2, Printer, ShoppingCart, History, Send, Wrench, AlertTriangle, AlertCircle, Users, Play, Square, Coffee, Check, MoreVertical, RefreshCw, Cloud, CheckCircle2, MessageSquare, FileText, UploadCloud, Download } from 'lucide-react';
import { MiniCalendar } from './MiniCalendar';
// 🗑️ [Phase 51 cleanup 2026-06-08] CollisionChecker retiré du formulaire BT
// (conflits d'horaire désormais gérés uniquement depuis le calendrier).
import { ProductSearchCombobox } from './ProductSearchCombobox';
import { filterEmployeesForBT } from '@/lib/btEmployees';
import { construirePayloadCreer } from '@/lib/btPayload';  // 🧱 Refactor BT Phase 3
// 🐛 [Phase v13.48 — 2026-07-14] Section UI Planification approximative (Sujet 4.B)
import { PlanificationApproxSection, persistPlanificationApprox } from './PlanificationApproxSection';
import { useBtEquipeMatriceCreer } from '@/hooks/useBtEquipeMatriceCreer';  // 🧱 Refactor BT Phase 3
import { BTFormProvider } from './BTFormContext';
import { SectionZonesCreer } from './creer/sections/SectionZonesCreer';  // 🧱 Refactor BT Phase 3
import { SectionEtapesCreer } from './creer/sections/SectionEtapesCreer';  // 🧱 Refactor BT Phase 3
import { SectionProduitsCreer } from './creer/sections/SectionProduitsCreer';  // 🧱 Refactor BT Phase 3
import { SectionEchafaudsCreer } from './creer/sections/SectionEchafaudsCreer';  // 🧱 Refactor BT Phase 3
import { SectionEquipementsCreer } from './creer/sections/SectionEquipementsCreer';  // 🧱 Refactor BT Phase 3
import { SectionLavagePressionCreer } from './creer/sections/SectionLavagePressionCreer';  // 🧱 Refactor BT Phase 3
import { SectionEquipeMatriceCreer } from './creer/sections/SectionEquipeMatriceCreer';  // 🧱 Refactor BT Phase 3
import { SectionDatesChantierCreer } from './creer/sections/SectionDatesChantierCreer';  // 🧱 Refactor BT Phase 3
import { SectionDescriptionPlanifCreer } from './creer/sections/SectionDescriptionPlanifCreer';  // 🧱 Refactor BT Phase 3
import { SectionClientDossierCreer } from './creer/sections/SectionClientDossierCreer';  // 🧱 Refactor BT Phase 3

export function CreerBonTravailDialog({ open, onClose, dossier, produits, employees, equipements, onCreated }) {
  // 🔄 Phase A2.2 (2026-05-04) — Item #4c : sous-traitants/installateurs
  // ne peuvent pas être assignés à un BT (règle Eliot). Helper centralisé.
  // Filtre par role_terrain ET type_employe pour couvrir les 2 champs historiques.
  const employesOriginal = employees || [];
  employees = filterEmployeesForBT(employesOriginal);
  const { isProprietaire } = useAuth();
  const [type, setType] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingZone, setUploadingZone] = useState(null);
  const [leaveConflicts, setLeaveConflicts] = useState([]);
  const [clientInfo, setClientInfo] = useState(null);
  const [eqDisponibilite, setEqDisponibilite] = useState({}); // {eq_id: {disponible, conflits}}
  const [showAddEmploye, setShowAddEmploye] = useState(false);
  // 🐛 [Phase v13.48 — 2026-07-14] State planification approximative (Sujet 4.B).
  //   Reste `null` par défaut à la création. Chargé sur les BT existants dans
  //   `EditerBonTravailDialog`. Le PUT/DELETE se fait après la création du BT
  //   principal (nécessite un `id`) — voir `doSave()` plus bas.
  const [planificationApprox, setPlanificationApprox] = useState(null);
  // 🆕 Phase C4 — dirty flag : true si l'utilisateur a modifié heure_debut manuellement.
  // Évite d'écraser sa saisie si le chef est ré-sélectionné ou changé.
  const [heureDebutDirty, setHeureDebutDirty] = useState(false);

  // Charger les infos du client — depuis l'API ou depuis le dossier comme fallback
  useEffect(() => {
    if (dossier?.client_id) {
      axios.get(`${API_URL}/api/crm/clients/${dossier.client_id}`)
        .then(res => res.data)
        .then(data => setClientInfo(data))
        .catch(() => {
          // Fallback: utiliser les infos du dossier directement
          setClientInfo({
            telephone: dossier.telephone_client || dossier.telephone || '',
            email: dossier.email_client || dossier.email || '',
            adresse_client: dossier.adresse_travaux || '',
            ville_client: dossier.ville_travaux || '',
            code_postal_client: dossier.code_postal || '',
          });
        });
    } else if (dossier) {
      // Pas de client_id — utiliser les infos du dossier
      setClientInfo({
        telephone: dossier.telephone_client || dossier.telephone || '',
        email: dossier.email_client || dossier.email || '',
        adresse_client: dossier.adresse_travaux || '',
        ville_client: dossier.ville_travaux || '',
        code_postal_client: dossier.code_postal || '',
      });
    }
  }, [dossier?.client_id, dossier?.id]);

  const soumissions = dossier.soumissions || [];
  const rawDesc = soumissions[0]?.description_travaux || '';
  const descriptionSoumission = rawDesc;
  const notesSoumission = soumissions[0]?.notes || '';

  const [form, setForm] = useState({
    description_travaux: descriptionSoumission,
    notes_speciales: notesSoumission,
    notes_importantes: '',
    notes_importantes_timing: 'debut_travaux',
    heures_budgetees: '',
    heure_debut: '',
    // 🔄 Phase B4 (Eliot, 2026-05-04) — Ce BT ne peut pas être reporté.
    // Affiché avec un emoji ⛔ dans le Calendrier de Production.
    dates_fixes: false,
    jours_pluie: false,
    pluie_date_debut: '',
    pluie_date_fin: '',
    chef_equipe_id: '',
    // TODO Phase 31 #1 adoption : remplacer le Select chef par <EmployeeSelector includeInactive={false}>.
    // Logique custom préservée (auto-fill employés équipe via chef_equipe_id parent).
    // Non bloquant car la liste actuelle filtre déjà `est_actif !== false` (intentionnel — on n'assigne pas un BT à un inactif).
    chef_equipe_nom: '',
    chef_dates_assignees: [], // 🆕 2026-04-30 : jours où le chef est présent (vide = tous les dates_chantier)
    chefs_par_jour: {}, // 🆕 2026-05 : { "YYYY-MM-DD": "<employee_id>" } — chef différent par jour (override chef_equipe_id)
    employes_assignes: [], // [{employee_id, employee_nom, dates_assignees: []}]
    produits_selectionnes: [],
    etapes: [],
    zones: [],
    dates_chantier: [],
    equipements_selectionnes: [], // [{equipement_id, equipement_nom, emoji, dates_utilisation: []}]
    // Nouveau: Lavage à pression
    lavage_pression: {
      necessaire: false,
      echafauds_a_monter: false,
      notes: '',
    },
    // Nouveau: Échafaudages (dans Équipements de location)
    echafaudages: {
      actif: false, // Activé soit par lavage_pression, soit manuellement
      source: '', // 'lavage' ou 'manuel'
      remorque_id: '',
      type_etage_id: '',
      quantite_etages: 0,
      date_montage: '', // Pour manuel seulement
      date_demontage: '',
      zone_id: null, // Associer à une zone du BT
      notes: '',
      pieces_calculees: [], // Résultat du calcul automatique
      pieces_manuelles: [], // Ajustements manuels
    },
  });

  const [calMonth, setCalMonth] = useState(new Date());
  const [equipeOccupee, setEquipeOccupee] = useState({});
  const [congesEquipe, setCongesEquipe] = useState({});
  const [allConges, setAllConges] = useState([]); // Congés approuvés globaux (pour warning cross-équipe)
  const [remorquesEchafauds, setRemorquesEchafauds] = useState([]);
  const [calculEnCours, setCalculEnCours] = useState(false);

  // Charger tous les congés approuvés pour détecter les conflits avec les employés assignés
  useEffect(() => {
    if (!open) return;
    axios.get(`${API_URL}/api/conges/employes-en-conge`).then(r => r.data)
      .then(data => setAllConges(Array.isArray(data) ? data : []))
      .catch(() => setAllConges([]));
  }, [open]);

  // Charger les remorques d'échafauds
  useEffect(() => {
    axios.get(`${API_URL}/api/unites-rangement`)
      .then(r => r.data)
      .then(data => {
        const remorques = (data.unites || []).filter(u => u.est_remorque_echafauds);
        setRemorquesEchafauds(remorques);
      })
      .catch(() => setRemorquesEchafauds([]));
  }, []);

  // Fonction pour calculer les pièces nécessaires
  const calculerPiecesEchafauds = async () => {
    const { remorque_id, type_etage_id, quantite_etages } = form.echafaudages;
    
    if (!remorque_id || !type_etage_id || quantite_etages <= 0) {
      toast.error("Veuillez sélectionner un type d'étage et une quantité");
      return;
    }

    setCalculEnCours(true);
    try {
      const res = await axios.post(`${API_URL}/api/echafaudages/calculer-pieces`, {
        remorque_id,
        type_etage_id,
        quantite_etages: parseInt(quantite_etages),
      });
      
      const { pieces_necessaires, tout_disponible } = res.data;
      
      setForm(f => ({
        ...f,
        echafaudages: {
          ...f.echafaudages,
          pieces_calculees: pieces_necessaires,
          pieces_manuelles: pieces_necessaires.map(p => ({
            nom: p.nom,
            quantite: p.quantite_necessaire
          }))
        }
      }));

      if (!tout_disponible) {
        toast.warning("⚠️ Certaines pièces ne sont pas disponibles en quantité suffisante");
      } else {
        toast.success("✅ Toutes les pièces sont disponibles");
      }
    } catch (err) {
      toast.error("Erreur lors du calcul");
      console.error(err);
    } finally {
      setCalculEnCours(false);
    }
  };

  // 🧱 Refactor BT Phase 3 — déplacé tel quel dans hooks/useBtEquipeMatriceCreer.js (ex-L204-438)
  const { addEmployeAssigne, handleChefChange, recalculerHeures, removeEmployeAssigne, setChefDuJour, toggleDate, toggleEmployeJour } = useBtEquipeMatriceCreer({ dossier, employees, form, heureDebutDirty, setCongesEquipe, setEquipeOccupee, setForm, setShowAddEmploye });
  // Vérification des congés de l'équipe pour les dates sélectionnées
  // 🆕 2026-05-18 — Ignore les dates passées (non actionnables, source de bruit)
  useEffect(() => {
    if (!form.chef_equipe_id || form.dates_chantier.length === 0) {
      setLeaveConflicts([]);
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    const futureDates = [...form.dates_chantier].filter(d => d >= today).sort();
    if (futureDates.length === 0) {
      setLeaveConflicts([]);
      return;
    }
    const dateDebut = futureDates[0];
    const dateFin = futureDates[futureDates.length - 1];
    axios.get(`${API_URL}/api/conges/employes-en-conge?date_debut=${dateDebut}&date_fin=${dateFin}`).then(r => r.data)
      .then(conges => {
        const teamIds = new Set([
          form.chef_equipe_id,
          // 🗑️ [v13.60.1] Filtre `!e.est_specialiste_lavage` retiré (P2 RCA).
          ...employees.filter(e => e.chef_equipe_id === form.chef_equipe_id && e.est_actif).map(e => e.id)
        ]);
        const filtered = conges.filter(c => {
          if (!teamIds.has(c.employee_id)) return false;
          const dFin = c.date_fin || c.date_debut;
          return !dFin || dFin >= today;
        });
        setLeaveConflicts(filtered);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.chef_equipe_id, JSON.stringify(form.dates_chantier)]);

  // Vérifier la disponibilité des équipements quand les dates changent
  useEffect(() => {
    if (form.dates_chantier.length === 0) { setEqDisponibilite({}); return; }
    const params = form.dates_chantier.map(d => `dates[]=${d}`).join('&');
    axios.get(`${API_URL}/api/equipements/disponibilite?${params}`).then(r => r.data)
      .then(d => setEqDisponibilite(d.disponibilite || {}))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(form.dates_chantier)]);

  // Équipements (extérieur)
  const addEquipement = (eqId) => {
    const eq = equipements.find(e => e.id === eqId);
    if (!eq || form.equipements_selectionnes.find(e => e.equipement_id === eqId)) return;
    setForm(f => ({ ...f, equipements_selectionnes: [...f.equipements_selectionnes, {
      equipement_id: eq.id, equipement_nom: eq.nom, emoji: eq.emoji || '', dates_utilisation: [...f.dates_chantier]
    }]}));
  };
  const removeEquipement = (idx) => setForm(f => ({ ...f, equipements_selectionnes: f.equipements_selectionnes.filter((_, i) => i !== idx) }));
  const toggleEquipDate = (eqIdx, dateStr) => {
    const updated = [...form.equipements_selectionnes];
    const eq = updated[eqIdx];
    eq.dates_utilisation = eq.dates_utilisation.includes(dateStr)
      ? eq.dates_utilisation.filter(d => d !== dateStr)
      : [...eq.dates_utilisation, dateStr].sort();
    setForm(f => ({ ...f, equipements_selectionnes: updated }));
  };

  // Produits
  const addProduit = () => setForm(f => ({ ...f, produits_selectionnes: [...f.produits_selectionnes, { produit_id: '', produit_nom: '', fini: '', unite: 'gallon', quantite: 1, quantite_prevue: 0, emplacement: '', code_couleur: '' }] }));
  const updateProduit = (idx, field, value) => {
    const updated = [...form.produits_selectionnes];
    updated[idx] = { ...updated[idx], [field]: value };
    if (field === 'produit_id') {
      const p = produits.find(pr => pr.id === value);
      if (p) {
        updated[idx].produit_nom = p.nom;
        if (p.unite) updated[idx].unite = p.unite;
        const finis = p.finis_disponibles || [];
        if (finis.length === 1) updated[idx].fini = finis[0];
        else if (finis.length === 0) updated[idx].fini = '';
      }
    }
    setForm(f => ({ ...f, produits_selectionnes: updated }));
  };
  const removeProduit = (idx) => setForm(f => ({ ...f, produits_selectionnes: f.produits_selectionnes.filter((_, i) => i !== idx) }));

  // Étapes
  const addEtape = () => setForm(f => ({ ...f, etapes: [...f.etapes, { numero: f.etapes.length + 1, description: '' }] }));
  const updateEtape = (idx, desc) => { const u = [...form.etapes]; u[idx] = { ...u[idx], description: desc }; setForm(f => ({ ...f, etapes: u })); };
  const removeEtape = (idx) => setForm(f => ({ ...f, etapes: f.etapes.filter((_, i) => i !== idx).map((e, i) => ({ ...e, numero: i + 1 })) }));

  // Zones
  const addZone = () => setForm(f => ({ ...f, zones: [...f.zones, { numero: f.zones.length + 1, nom: '', description: '', photo_url: null }] }));
  const updateZone = (idx, field, value) => { const u = [...form.zones]; u[idx] = { ...u[idx], [field]: value }; setForm(f => ({ ...f, zones: u })); };
  const removeZone = (idx) => setForm(f => ({ ...f, zones: f.zones.filter((_, i) => i !== idx).map((z, i) => ({ ...z, numero: i + 1 })) }));

  const handleZonePhotoUpload = async (idx, file) => {
    setUploadingZone(idx);
    try {
      const { compressImageIfNeeded } = await import('@/lib/imageCompression');
      const compressed = await compressImageIfNeeded(file);
      const formData = new FormData();
      formData.append('file', compressed);
      const res = await axios.post(`${API_URL}/api/crm/upload-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.status !== 200) throw new Error('Upload failed');
      const data = res.data;
      updateZone(idx, 'photo_url', data.photo_url);
      toast.success('Photo uploadée');
    } catch (e) { toast.error('Erreur upload'); }
    finally { setUploadingZone(null); }
  };

  // Calcule la liste des conflits de congé (employés assignés en congé approuvé pendant dates_chantier)
  const computeConflictsConge = () => {
    if (form.dates_chantier.length === 0 || allConges.length === 0) return [];
    const assignesIds = new Set([
      ...(form.chef_equipe_id ? [form.chef_equipe_id] : []),
      ...form.employes_assignes.map(e => e.employee_id),
    ]);
    if (assignesIds.size === 0) return [];
    const datesSet = new Set(form.dates_chantier);
    const conflits = {}; // { employee_nom: [dates] }
    for (const c of allConges) {
      if (!assignesIds.has(c.employee_id)) continue;
      if (!c.date_debut) continue;
      const d0 = new Date(c.date_debut + 'T12:00:00');
      const d1 = new Date((c.date_fin || c.date_debut) + 'T12:00:00');
      for (let d = new Date(d0); d <= d1; d.setDate(d.getDate() + 1)) {
        const ds = d.toISOString().split('T')[0];
        if (datesSet.has(ds)) {
          const nom = c.employe_nom || c.employee_nom || 'Employé';
          if (!conflits[nom]) conflits[nom] = [];
          conflits[nom].push(ds);
        }
      }
    }
    return Object.entries(conflits).map(([nom, dates]) => ({ nom, dates: dates.slice().sort() }));
  };
  const [showConfirmConge, setShowConfirmConge] = useState(false);
  const [conflitsConge, setConflitsConge] = useState([]);

  const handleSave = async () => {
    if (!form.description_travaux.trim()) { toast.error('Description requise'); return; }
    // Double-validation si conflit de congé détecté
    const conflits = computeConflictsConge();
    if (conflits.length > 0 && !showConfirmConge) {
      setConflitsConge(conflits);
      setShowConfirmConge(true);
      return;
    }
    setShowConfirmConge(false);
    await doSave();
  };

  const doSave = async () => {
    setSaving(true);
    try {
      // 🧱 Refactor BT Phase 3 — construction du payload déplacée telle quelle dans lib/btPayload.js
      const payload = construirePayloadCreer({ form, employees, dossier, soumissions, type });
      const res = await axios.post(`${API_URL}/api/crm/bons-travail`, payload);
      if (res.status !== 200) throw new Error('Erreur');
      // 🐛 [Phase v13.48 — 2026-07-14] Persistance de la planification approximative
      //   après création du BT (nécessite un `id`). Silent-fail : on n'échoue pas
      //   la création BT si l'endpoint planif retourne une erreur.
      const btIdCree = res.data?.id;
      if (btIdCree && planificationApprox) {
        try {
          await persistPlanificationApprox(btIdCree, planificationApprox, null);
        } catch (errPlanif) {
          console.error('Erreur persist planification approximative:', errPlanif);
          toast.warning('Bon créé mais la planification approximative n\'a pas été sauvegardée. Réessayez depuis l\'éditeur.');
        }
      }
      toast.success('Bon de travail créé!');
      // 🆕 2026-05-08 — Alerte si le BT inclut des jours de weekend
      const weekendDates = (form.dates_chantier || []).filter(d => {
        const day = new Date(d + 'T12:00:00').getDay();
        return day === 0 || day === 6;
      });
      if (weekendDates.length > 0) {
        const labels = weekendDates.slice(0, 5).map(d => {
          const dt = new Date(d + 'T12:00:00');
          const jour = dt.getDay() === 0 ? 'Dim.' : 'Sam.';
          return `${jour} ${d}`;
        }).join(', ');
        const extra = weekendDates.length > 5 ? `, … (+${weekendDates.length - 5})` : '';
        toast.warning(
          `🟡 Ce BT est cédulé sur ${weekendDates.length} jour(s) de weekend : ${labels}${extra}. Vérifiez les taux temps double / prime CCQ avant la paie.`,
          { duration: 10000 }
        );
      }
      onCreated();
    } catch (e) { console.error(e); toast.error('Erreur lors de la création'); }
    finally { setSaving(false); }
  };

  // Step 1: Type selection
  if (!type) {
  return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Créer un Bon de Travail</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600 mb-4">Quel type de bon de travail souhaitez-vous créer ?</p>
          <div className="grid grid-cols-2 gap-4">
            <button onClick={() => setType('interieur')} className="p-6 border-2 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-center group" data-testid="btn-type-interieur">
              <Home className="w-10 h-10 mx-auto mb-3 text-blue-500 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-lg">Intérieur</h3>
              <p className="text-xs text-gray-500 mt-1">Peinture de pièces, cuisines, salons...</p>
            </button>
            <button onClick={() => setType('exterieur')} className="p-6 border-2 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all text-center group" data-testid="btn-type-exterieur">
              <TreePine className="w-10 h-10 mx-auto mb-3 text-green-500 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-lg">Extérieur</h3>
              <p className="text-xs text-gray-500 mt-1">Revêtement, façades, zones numérotées...</p>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const isExt = type === 'exterieur';
  const emplacementLabel = isExt ? 'Surface' : 'Pièce';

  // 🧱 Refactor BT Phase 3 — état + handlers exposés aux sections via BTFormProvider (extra)
  // Incident I-1 : bloc déplacé hors du `if (!type)` et après `emplacementLabel` (portée + TDZ).
  const ctxCreer = {
    addEmployeAssigne,
    addEquipement,
    addEtape,
    addProduit,
    addZone,
    allConges,
    calMonth,
    calculEnCours,
    calculerPiecesEchafauds,
    clientInfo,
    congesEquipe,
    doSave,
    dossier,
    emplacementLabel,
    employees,
    eqDisponibilite,
    equipeOccupee,
    equipements,
    form,
    handleChefChange,
    handleZonePhotoUpload,
    isExt,
    isProprietaire,
    leaveConflicts,
    notesSoumission,
    planificationApprox,
    produits,
    remorquesEchafauds,
    removeEmployeAssigne,
    removeEquipement,
    removeEtape,
    removeProduit,
    removeZone,
    setCalMonth,
    setChefDuJour,
    setForm,
    setHeureDebutDirty,
    setPlanificationApprox,
    setShowAddEmploye,
    showAddEmploye,
    toggleDate,
    toggleEmployeJour,
    toggleEquipDate,
    type,
    updateEtape,
    updateProduit,
    updateZone,
    uploadingZone,
  };

  return (
    /* 🧱 Refactor BT Phase 3 — T3 : BTFormProvider expose l'état + handlers aux sections (extra) */
    <BTFormProvider form={form} setForm={setForm} employees={employees} bonId={null} extra={ctxCreer}>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isExt ? <TreePine className="w-5 h-5 text-green-600" /> : <Home className="w-5 h-5 text-blue-600" />}
            Bon de Travail {isExt ? 'Extérieur' : 'Intérieur'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <SectionClientDossierCreer />
          <SectionDescriptionPlanifCreer />
          <SectionDatesChantierCreer />
          <SectionEquipeMatriceCreer />
          <SectionLavagePressionCreer />
          <SectionEquipementsCreer />
          <SectionEchafaudsCreer />
          <SectionProduitsCreer />
          <SectionEtapesCreer />
          <SectionZonesCreer />
          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => { if (type) setType(null); else onClose(); }}>{type ? 'Retour' : 'Annuler'}</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-save-bon">
              {saving ? 'Enregistrement...' : 'Créer le Bon de Travail'}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Dialog de confirmation — conflit de congé détecté */}
      <Dialog open={showConfirmConge} onOpenChange={setShowConfirmConge}>
        <DialogContent className="max-w-md" data-testid="dialog-confirm-conge">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Conflit de congé détecté
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              {conflitsConge.length === 1
                ? 'Un employé assigné est en congé approuvé pendant la période :'
                : `${conflitsConge.length} employés assignés sont en congé approuvé pendant la période :`}
            </p>
            <ul className="space-y-1 bg-red-50 border border-red-200 rounded-lg p-3">
              {conflitsConge.map((c) => (
                <li key={c.nom} className="text-sm text-red-800">
                  <strong>{c.nom}</strong>
                  <span className="text-red-600 text-xs ml-1">
                    ({c.dates.map(d => new Date(d + 'T12:00:00').toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' })).join(', ')})
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500 italic">
              Voulez-vous quand même créer ce BT ? Cette action sera enregistrée telle quelle.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowConfirmConge(false)} data-testid="btn-confirm-conge-cancel">
                Annuler et modifier
              </Button>
              <Button
                onClick={async () => { setShowConfirmConge(false); await doSave(); }}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="btn-confirm-conge-force"
              >
                Créer quand même
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
    </BTFormProvider>
  );
}
